/**
 * Recruiting API Contract
 *
 * oRPC contracts for the applicant-reviewer API Gateway.
 * These contracts wrap the external recruiting REST API so that the
 * frontend can call it via a typed oRPC client using OpenAPILink.
 */
import { oc } from "@orpc/contract";
import { z } from "zod";
import { 
// Campaign schemas
HiringCampaignSchema, CreateCampaignInputSchema, UpdateCampaignInputSchema, ListCampaignsResponseSchema, 
// Source schemas
RecruitmentSourceSchema, CreateSourceInputSchema, UpdateSourceInputSchema, ListSourcesResponseSchema, DeleteSourceResponseSchema, 
// Dashboard schemas
DashboardResponseSchema, 
// Job schemas
JobPostingSchema, CreateJobInputSchema, ListJobsResponseSchema, 
// Pipeline / Application schemas
ApplicationSummarySchema, PipelineGroupedResponseSchema, UpdateStatusInputSchema, 
// Interview schemas
InterviewSchema, CreateInterviewInputSchema, ListInterviewsResponseSchema, UpdateInterviewInputSchema, 
// Manager applications schemas
ListManagerAppsQuerySchema, ListApplicationsResponseSchema, 
// Offer / Hire schemas
OfferInputSchema, HireResponseSchema, 
// Candidate profile schemas
CandidateProfileSchema, 
// Manager media schemas
ManagerMediaSchema, ListManagerMediaResponseSchema, 
// Manager availability schemas
ManagerAvailabilitySchema, } from "@hourly/api/recruitingDemo";
// ============================================
// Campaign Contracts
// ============================================
export const listCampaigns = oc
    .route({
    method: "GET",
    path: "/campaigns",
    summary: "List hiring campaigns",
    tags: ["Recruiting - Campaigns"],
})
    .input(z.object({}))
    .output(ListCampaignsResponseSchema);
export const getCampaign = oc
    .route({
    method: "GET",
    path: "/campaigns/{campaignId}",
    summary: "Get a campaign by ID",
    tags: ["Recruiting - Campaigns"],
})
    .input(z.object({ campaignId: z.uuid() }))
    .output(HiringCampaignSchema);
export const createCampaign = oc
    .route({
    method: "POST",
    path: "/campaigns",
    summary: "Create a hiring campaign",
    tags: ["Recruiting - Campaigns"],
    successStatus: 201,
})
    .input(CreateCampaignInputSchema)
    .output(HiringCampaignSchema);
export const updateCampaign = oc
    .route({
    method: "PUT",
    path: "/campaigns/{campaignId}",
    summary: "Update a hiring campaign",
    tags: ["Recruiting - Campaigns"],
})
    .input(z.object({ campaignId: z.uuid() }).merge(UpdateCampaignInputSchema))
    .output(HiringCampaignSchema);
// ============================================
// Source Contracts
// ============================================
export const listSources = oc
    .route({
    method: "GET",
    path: "/campaigns/{campaignId}/sources",
    summary: "List sources for a campaign",
    tags: ["Recruiting - Sources"],
})
    .input(z.object({ campaignId: z.uuid() }))
    .output(ListSourcesResponseSchema);
export const createSource = oc
    .route({
    method: "POST",
    path: "/campaigns/{campaignId}/sources",
    summary: "Create a recruitment source",
    tags: ["Recruiting - Sources"],
    successStatus: 201,
})
    .input(z.object({ campaignId: z.uuid() }).merge(CreateSourceInputSchema))
    .output(RecruitmentSourceSchema);
export const updateSource = oc
    .route({
    method: "PUT",
    path: "/campaigns/{campaignId}/sources/{sourceId}",
    summary: "Update a recruitment source",
    tags: ["Recruiting - Sources"],
})
    .input(z
    .object({ campaignId: z.uuid(), sourceId: z.uuid() })
    .merge(UpdateSourceInputSchema))
    .output(RecruitmentSourceSchema);
export const deleteSource = oc
    .route({
    method: "DELETE",
    path: "/campaigns/{campaignId}/sources/{sourceId}",
    summary: "Delete a recruitment source",
    tags: ["Recruiting - Sources"],
})
    .input(z.object({ campaignId: z.uuid(), sourceId: z.uuid() }))
    .output(DeleteSourceResponseSchema);
// ============================================
// Dashboard Contracts
// ============================================
export const getDashboard = oc
    .route({
    method: "GET",
    path: "/dashboard",
    summary: "Get recruiting dashboard KPIs and pipeline counts",
    tags: ["Recruiting - Dashboard"],
})
    .input(z.object({}))
    .output(DashboardResponseSchema);
// ============================================
// Job Contracts
// ============================================
export const listJobs = oc
    .route({
    method: "GET",
    path: "/jobs",
    summary: "List job postings",
    tags: ["Recruiting - Jobs"],
})
    .input(z.object({
    status: z.string().optional(),
    location: z.string().optional(),
    roleName: z.string().optional(),
    search: z.string().optional(),
}))
    .output(ListJobsResponseSchema);
export const createJob = oc
    .route({
    method: "POST",
    path: "/jobs",
    summary: "Create a job posting",
    tags: ["Recruiting - Jobs"],
    successStatus: 201,
})
    .input(CreateJobInputSchema)
    .output(JobPostingSchema);
export const updateJob = oc
    .route({
    method: "PUT",
    path: "/jobs/{jobId}",
    summary: "Update a job posting",
    tags: ["Recruiting - Jobs"],
})
    .input(z.object({ jobId: z.uuid() }).merge(CreateJobInputSchema.partial()))
    .output(JobPostingSchema);
export const deleteJob = oc
    .route({
    method: "DELETE",
    path: "/jobs/{jobId}",
    summary: "Delete a job posting",
    tags: ["Recruiting - Jobs"],
})
    .input(z.object({ jobId: z.uuid() }))
    .output(z.object({ deleted: z.literal(true), jobId: z.uuid() }));
// ============================================
// Pipeline Contracts
// ============================================
export const getPipeline = oc
    .route({
    method: "GET",
    path: "/pipeline",
    summary: "Get pipeline grouped by application status",
    tags: ["Recruiting - Pipeline"],
})
    .input(z.object({}))
    .output(PipelineGroupedResponseSchema);
export const updateApplicationStatus = oc
    .route({
    method: "PATCH",
    path: "/applications/{applicationId}/status",
    summary: "Update an application status",
    tags: ["Recruiting - Pipeline"],
})
    .input(z.object({ applicationId: z.uuid() }).merge(UpdateStatusInputSchema))
    .output(ApplicationSummarySchema);
// ============================================
// Interview Contracts
// ============================================
export const listInterviews = oc
    .route({
    method: "GET",
    path: "/interviews",
    summary: "List interviews (upcoming and past pending feedback)",
    tags: ["Recruiting - Interviews"],
})
    .input(z.object({}))
    .output(ListInterviewsResponseSchema);
export const createInterview = oc
    .route({
    method: "POST",
    path: "/interviews",
    summary: "Create an interview",
    tags: ["Recruiting - Interviews"],
    successStatus: 201,
})
    .input(CreateInterviewInputSchema)
    .output(InterviewSchema);
// ============================================
// Manager Applications Contracts
// ============================================
export const listManagerApplications = oc
    .route({
    method: "GET",
    path: "/manager-applications",
    summary: "List applications visible to managers",
    tags: ["Recruiting - Manager Applications"],
})
    .input(ListManagerAppsQuerySchema)
    .output(ListApplicationsResponseSchema);
export const getManagerApplication = oc
    .route({
    method: "GET",
    path: "/manager-applications/{applicationId}",
    summary: "Get a single application for manager review",
    tags: ["Recruiting - Manager Applications"],
})
    .input(z.object({ applicationId: z.uuid() }))
    .output(ApplicationSummarySchema);
// ============================================
// Offer / Hire Contracts
// ============================================
export const sendOffer = oc
    .route({
    method: "POST",
    path: "/applications/{applicationId}/offer",
    summary: "Send an offer to an applicant",
    tags: ["Recruiting - Pipeline"],
    successStatus: 201,
})
    .input(z.object({ applicationId: z.uuid() }).merge(OfferInputSchema))
    .output(ApplicationSummarySchema);
export const hireApplicant = oc
    .route({
    method: "POST",
    path: "/applications/{applicationId}/hire",
    summary: "Mark an applicant as hired",
    tags: ["Recruiting - Pipeline"],
})
    .input(z.object({ applicationId: z.uuid() }))
    .output(HireResponseSchema);
// ============================================
// Candidate Profile Contracts
// ============================================
export const getCandidateProfile = oc
    .route({
    method: "GET",
    path: "/manager-candidates/{candidateId}/profile",
    summary: "Get a candidate profile for manager review",
    tags: ["Recruiting - Candidates"],
})
    .input(z.object({ candidateId: z.uuid() }))
    .output(CandidateProfileSchema);
// ============================================
// Manager Media Contracts
// ============================================
export const listManagerMedia = oc
    .route({
    method: "GET",
    path: "/manager-media",
    summary: "List media files by application or candidate",
    tags: ["Recruiting - Media"],
})
    .input(z.object({
    applicationId: z.uuid().optional(),
    candidateId: z.uuid().optional(),
}))
    .output(ListManagerMediaResponseSchema);
export const getManagerMedia = oc
    .route({
    method: "GET",
    path: "/manager-media/{mediaId}",
    summary: "Get a media file with presigned download URL",
    tags: ["Recruiting - Media"],
})
    .input(z.object({ mediaId: z.uuid() }))
    .output(ManagerMediaSchema);
// ============================================
// Manager Availability Contracts
// ============================================
export const getManagerAvailability = oc
    .route({
    method: "GET",
    path: "/manager-availability",
    summary: "Get manager weekly availability slots",
    tags: ["Recruiting - Availability"],
})
    .input(z.object({}))
    .output(ManagerAvailabilitySchema);
export const updateManagerAvailability = oc
    .route({
    method: "PUT",
    path: "/manager-availability",
    summary: "Update manager weekly availability slots",
    tags: ["Recruiting - Availability"],
})
    .input(ManagerAvailabilitySchema)
    .output(ManagerAvailabilitySchema);
// ============================================
// Interview Contracts (extended)
// ============================================
export const getInterview = oc
    .route({
    method: "GET",
    path: "/interviews/{interviewId}",
    summary: "Get a single interview",
    tags: ["Recruiting - Interviews"],
})
    .input(z.object({ interviewId: z.uuid() }))
    .output(InterviewSchema);
export const updateInterview = oc
    .route({
    method: "PUT",
    path: "/interviews/{interviewId}",
    summary: "Update an interview",
    tags: ["Recruiting - Interviews"],
})
    .input(z.object({ interviewId: z.uuid() }).merge(UpdateInterviewInputSchema))
    .output(InterviewSchema);
// ============================================
// Contract Router
// ============================================
export const recruitingContract = {
    // Campaigns
    listCampaigns,
    getCampaign,
    createCampaign,
    updateCampaign,
    // Sources
    listSources,
    createSource,
    updateSource,
    deleteSource,
    // Dashboard
    getDashboard,
    // Jobs
    listJobs,
    createJob,
    updateJob,
    deleteJob,
    // Pipeline
    getPipeline,
    updateApplicationStatus,
    sendOffer,
    hireApplicant,
    // Interviews
    listInterviews,
    createInterview,
    getInterview,
    updateInterview,
    // Manager Applications
    listManagerApplications,
    getManagerApplication,
    // Candidates
    getCandidateProfile,
    // Media
    listManagerMedia,
    getManagerMedia,
    // Availability
    getManagerAvailability,
    updateManagerAvailability,
};
//# sourceMappingURL=recruiting.js.map